import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tooltip } from "bootstrap";
import logo1 from "../assets/CPA1.png";
import question from "../assets/helpcp.png";
import bell from "../assets/bellcp.png";
import bellActive from "../assets/Group.png";
import home from "../assets/homecp.png";
import "./Dashboard.css";
import { FiUser } from "react-icons/fi";
import { API_BASE } from "../apiConfig";

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;
const LAST_ACTIVITY_KEY = "lastActivityTime";
const NAV_PAGES = ["/Home", "/start-assessment", "/continue-assessment", "/Suggested-interventions", "/Goals", "/Library", "/Reminder"];
const HOME_NAV = [
  { label: "Home", to: "/Home" },
  {
    label: "Assessments",
    menu: "assessment",
    dropdown: [
      { label: "Start Initial Client Assessment", to: "/start-assessment?appointment=Initial&menu=assessment" },
      { label: "Continue Incomplete Assessment", to: "/continue-assessment?menu=assessment" },
    ],
  }, {
    label: "Goals",
    menu: "goals",
    dropdown: [
      { label: "Review Existing Goals", to: "/Goals?menu=goals" },
      { label: "Set New Goal for Existing Client", to: "/start-assessment?appointment=Review&menu=goals" },
    ],
  },
  { label: "Suggested Interventions", to: "/Suggested-interventions" },
  { label: "Library", to: "/Library" },
  { label: "Reminders", to: "/Reminder" },
];

const FULL_NAV = [
  { label: "Home", to: "/Home" },
  {
    label: "Assessments",
    menu: "assessment",
    dropdown: [
      { label: "Start Initial Client Assessment", to: "/start-assessment?appointment=Initial&menu=assessment" },
      { label: "Continue Incomplete Assessment", to: "/continue-assessment?menu=assessment" },
    ],
  }, {
    label: "Goals",
    menu: "goals",
    dropdown: [
      { label: "Review Existing Goals", to: "/Goals?menu=goals" },
      { label: "Set New Goal for Existing Client", to: "/start-assessment?appointment=Review&menu=goals" },
    ],
  },
  { label: "Suggested Interventions", to: "/Suggested-interventions" },
  { label: "Library", to: "/Library" },
  { label: "Reminders", to: "/Reminder" },
];

const HOME_NAV_PAGES = ["/Home"];

const Header = ({ onToggleMenu, menuOpen, setMenuOpen }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showSessionExpiredPopup, setShowSessionExpiredPopup] = useState(false);
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const popupRef = useRef(null);
  const detailRef = useRef(null);
  const detailCloseBtnRef = useRef(null);
  const sessionExpiredBtnRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/Home";
  const sessionExpiredShownRef = useRef(false);

  // Effective menu state: support external props when provided,
  // otherwise manage the drawer internally (App renders <Header /> without props).
  const isMenuControlled = typeof setMenuOpen === "function";
  const isMenuOpen = isMenuControlled ? Boolean(menuOpen) : internalMenuOpen;

  const closeMenu = () => {
    if (isMenuControlled) {
      setMenuOpen(false);
    } else {
      setInternalMenuOpen(false);
    }
  };

  const handleMenuToggle = () => {
    if (typeof onToggleMenu === "function") {
      onToggleMenu();
    } else if (isMenuControlled) {
      setMenuOpen(prev => !prev);
    } else {
      setInternalMenuOpen(prev => !prev);
    }
  };

  // Close the mobile drawer when leaving the mobile viewport
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 767 && isMenuOpen) closeMenu();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMenuOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  // Fetch Notifications from API
  useEffect(() => {
    const fetchNotifications = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await fetch(`${API_BASE}/therapist_api/get_notifications.php`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (data.status === "success" && data.data) {
          setNotifications(data.data);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();
  }, []);

  // Helper to format date to "27 Aug 2026"
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  };

  // Mark a notification as read on the server
  const markNotificationRead = async (id) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      await fetch(`${API_BASE}/therapist_api/mark_as_read.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ notification_id: id })
      });
      setNotifications(prev => prev.map(n =>
        n.notification_id === id ? { ...n, is_read: 1 } : n
      ));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Get only the latest 100 notifications
  const visibleNotifications = notifications.slice(0, 100);

  // Calculate unread count based on visible notifications
  const unreadCount = visibleNotifications.filter(n => Number(n.is_read) !== 1).length;

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltipTriggerList.forEach((el) => {
      if (!el._tooltipInstance) {
        const t = new Tooltip(el);
        el._tooltipInstance = t;
      }
    });
    return () => {
      tooltipTriggerList.forEach((el) => {
        if (el._tooltipInstance) {
          el._tooltipInstance.dispose();
          delete el._tooltipInstance;
        }
      });
    };
  }, []);

  // close user popup and notification popup if click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) && !e.target.closest(".user-profile")) {
        setShowPopup(false);
      }
      if (!e.target.closest(".notification-popup") && !e.target.closest(".nav-icon-btn")) {
        setShowNotifications(false);
        setSelectedNotification(null);
      }
      if (!e.target.closest(".nav-item-dropdown")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get user details from localStorage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userDetails = {
    name: user.first_name ? `${user.first_name} ${user.last_name || ""}` : "",
    email: user.email || "",
    profile_image: user.profile_image || null
  };

  // Construct full image URL if available
  const baseUrl = `${API_BASE}/uploads/therapists/`;
  const profileImageUrl = userDetails.profile_image ? `${baseUrl}${userDetails.profile_image}` : null;

  // Helper function to parse JWT
  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return null;
    }
  };

  // Helper function to check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    const decoded = parseJwt(token);
    if (!decoded || !decoded.exp) return false;
    return Date.now() >= decoded.exp * 1000;
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("token");

    if (isTokenExpired(token)) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      navigate("/");
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/therapist_api/therapist_logout.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.status) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/");
      } else {
        console.error("Logout failed:", data.message);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/");
      }
    } catch (error) {
      console.error("Logout error:", error);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      navigate("/");
    }
  };

  // Called when the user acknowledges the "Session expired" popup - this is
  // what actually clears storage and redirects to login now, instead of that
  // happening silently in the background.
  const handleSessionExpiredConfirm = () => {
    setShowSessionExpiredPopup(false);
    sessionExpiredShownRef.current = false;
    sessionStorage.removeItem(LAST_ACTIVITY_KEY);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  // -- SESSION EXPIRY: 15-minute inactivity timer --------------------------
  // The session expires when the user has been INACTIVE (no mouse, keyboard,
  // touch, or scroll events) for 15 minutes AND then returns to the tab.
  // We also check JWT expiry as a hard ceiling ? if the JWT itself expired
  // the session is over regardless of activity.
  //
  // Flow:
  //   1. Every user interaction resets `lastActivityTime` in sessionStorage.
  //   2. On `visibilitychange` (tab becomes visible), we check:
  //        a) Is the JWT expired? ? show popup
  //        b) Has 15+ minutes elapsed since last activity? ? show popup
  //   3. A custom `session-expired` event can be dispatched from any
  //      component when an API returns "Invalid or expired token".
  // -------------------------------------------------------------------------

  const resetLastActivity = useCallback(() => {
    try {
      sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    } catch (_) { /* ignore */ }
  }, []);

  const getElapsedSinceLastActivity = useCallback(() => {
    try {
      const ts = sessionStorage.getItem(LAST_ACTIVITY_KEY);
      if (!ts) return 0;
      return Date.now() - Number(ts);
    } catch (_) {
      return 0;
    }
  }, []);

  const shouldShowSessionExpired = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) return false;
    if (isTokenExpired(token)) return true;
    return getElapsedSinceLastActivity() >= INACTIVITY_TIMEOUT_MS;
  }, [getElapsedSinceLastActivity]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerSessionExpired = useCallback(() => {
    if (sessionExpiredShownRef.current) return;
    sessionExpiredShownRef.current = true;
    setShowSessionExpiredPopup(true);
  }, []);

  // Track user activity ? reset timer on any interaction
  useEffect(() => {
    resetLastActivity();
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    const handler = () => resetLastActivity();
    events.forEach((e) => document.addEventListener(e, handler, { passive: true }));
    return () => events.forEach((e) => document.removeEventListener(e, handler));
  }, [resetLastActivity]);

  // When the browser tab / app becomes visible again, check session status
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (shouldShowSessionExpired()) {
        triggerSessionExpired();
      } else {
        sessionExpiredShownRef.current = false;
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [shouldShowSessionExpired, triggerSessionExpired]);

  // Listen for custom "session-expired" events dispatched by API callers
  useEffect(() => {
    const handler = () => triggerSessionExpired();
    window.addEventListener("session-expired", handler);
    return () => window.removeEventListener("session-expired", handler);
  }, [triggerSessionExpired]);

  // Handle clicking a notification item
  const handleNotificationClick = (n) => {
    setSelectedNotification(n);
    if (Number(n.is_read) !== 1) {
      markNotificationRead(n.notification_id);
    }
  };

  // Handler for Key Down events (Tab, Enter, Space)
  const handleKeyDown = (action, e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  // Focus the close button when notification detail dialog opens
  useEffect(() => {
    if (selectedNotification && detailCloseBtnRef.current) {
      detailCloseBtnRef.current.focus();
    }
  }, [selectedNotification]);

  // Focus the OK button when session expired dialog opens
  useEffect(() => {
    if (showSessionExpiredPopup && sessionExpiredBtnRef.current) {
      sessionExpiredBtnRef.current.focus();
    }
  }, [showSessionExpiredPopup]);

  return (
    <>
      <header className="header shadow-sm bg-white" role="banner">
        {/* Mobile Toggler */}
        <button
          type="button"
          className="mobile-toggler"
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMenuOpen}
          onClick={handleMenuToggle}
          onKeyDown={(e) => handleKeyDown(handleMenuToggle, e)}

        >
          <i className={`fas ${isMenuOpen ? "fa-times" : "fa-bars"}`} aria-hidden="true"></i>
        </button>

        {/* Logo */}
        <button
          className="header-logo d-flex align-items-center"
          onClick={() => navigate("/Home")}
          aria-label="Go to Home"
        >
          <img src={logo1} alt="CPA Logo" className="header-logo-img" />
        </button>

        <div className="header-actions1 d-flex align-items-center">

          {/* Container for the 3 icons */}
          <div className="d-flex align-items-center gap-3">

            {/* Home Icon */}
            {!isHomePage && (
              <button
                className="nav-icon-btn"
                onClick={() => navigate("/Home")}
                aria-label="Home"
              >
                <img src={home} className="nav-icon-img" alt="" aria-hidden="true" />
                <span className="nav-icon-text">Home</span>
              </button>
            )}

            {/* Notification Icon */}
            <button
              className="nav-icon-btn notification-btn"
              onClick={() => { setShowNotifications(!showNotifications); setSelectedNotification(null); }}
              aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
              aria-expanded={showNotifications}
            >
              <img
                src={showNotifications ? bellActive : bell}
                className="nav-icon-img"
                alt=""
                aria-hidden="true"
              />
              <span className="nav-icon-text">Notifications</span>

              {/* Notification Count Badge */}
              {unreadCount > 0 && (
                <span className="notification-badge">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Help Icon */}
            <button
              className="nav-icon-btn"
              onClick={() => navigate("/Help")}
              aria-label="Help"
            >
              <img src={question} className="nav-icon-img" alt="" aria-hidden="true" />
              <span className="nav-icon-text">Help</span>
            </button>

          </div>

          {/* User Profile */}
          <button
            className={`action-item user-profile d-flex align-items-center ${showPopup ? "active" : ""}`}
            onClick={() => setShowPopup((p) => !p)}
            aria-label={`User Name ${userDetails.name}`}
            aria-expanded={showPopup}
          >
            {profileImageUrl ? (
              <img
                src={profileImageUrl}
                alt="Profile"
                className="user user-avatar"
              />
            ) : (
              <FiUser className="user" aria-hidden="true" />
            )}
            <span className="user-name">{userDetails?.name || ""}</span>
            <i className={`fas fa-caret-${showPopup ? "up" : "down"} ms-2 user-caret`} aria-hidden="true"></i>
          </button>
        </div>

        {/* User Popup */}
        {showPopup && (
          <div className="user-popup1" ref={popupRef} onKeyDown={(e) => { if (e.key === 'Escape') setShowPopup(false); }}>
            <div className="popup-content">
              <div className="details">
                <h5>{userDetails.name}</h5>
                <p>{userDetails.email}</p>
              </div>
              <button
                className="logout-link"
                onClick={() => { setShowPopup(false); handleLogout(); }}
                aria-label="Logout"
              >
                <div className="logout-btn">
                  <p className="logout-btn1">Logout</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Notification Popup Container */}
        {showNotifications && !selectedNotification && (
          <div className="notification-popup-wrapper" role="region" aria-label="Notifications" onKeyDown={(e) => { if (e.key === 'Escape') { setShowNotifications(false); setSelectedNotification(null); } }}>
            <div className="notification-popup shadow-lg">
              <div className="popup-header">
                <h5>Notifications</h5>
                <button
                  className="close-btn"
                  aria-label="Close notifications"
                  onClick={() => setShowNotifications(false)}
                >
                  &#10005;
                </button>
              </div>

              <div className="popup-body">
                {visibleNotifications.length > 0 ? (
                  visibleNotifications.map((n) => {
                    const isRead = Number(n.is_read) === 1;

                    return (
                      <div
                        key={n.notification_id}
                        className={`notification-item ${isRead ? '' : 'unread'}`}
                        role="button"
                        tabIndex={0}
                        aria-label={`Notification: ${n.message}`}
                        onClick={() => handleNotificationClick(n)}
                        onKeyDown={(e) => handleKeyDown(() => handleNotificationClick(n), e)}
                      >
                        <p className="notification-date">
                          {formatDate(n.sent_date)}
                        </p>

                        <p className="notification-text">
                          {n.message}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <div className="notification-item">
                    <p className="notification-text">No notifications found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notification Detail Popup with Blur Background */}
        {selectedNotification && (
          <div
            className="notification-detail-overlay"
            ref={detailRef}
            role="dialog"
            aria-modal="true"
            aria-label="Notification details"
            onClick={() => setSelectedNotification(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSelectedNotification(null);
                return;
              }
              if (e.key === 'Tab') {
                e.preventDefault();
                if (detailCloseBtnRef.current) {
                  detailCloseBtnRef.current.focus();
                }
              }
            }}
          >
            <div
              className="notification-detail-popup shadow"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="detail-header">
                <h6>Notification Details</h6>
                <button
                  className="detail-close-btn"
                  aria-label="Close notification detail"
                  ref={detailCloseBtnRef}
                  onClick={() => setSelectedNotification(null)}
                >
                  &#10005;
                </button>
              </div>
              <div className="notification-content">
                <p className="detail-text">{selectedNotification.message}</p>
                <p className="notification-date detail-date">{formatDate(selectedNotification.sent_date)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Session Expired Popup */}
        {showSessionExpiredPopup && (
          <div
            className="session-expired-overlay"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="session-expired-title"
            aria-describedby="session-expired-desc"
            onKeyDown={(e) => { if (e.key === 'Escape') handleSessionExpiredConfirm(); }}
          >
            <div
              className="session-expired-popup shadow"
            >
              <h6 id="session-expired-title">Session Expired</h6>
              <p id="session-expired-desc" className="session-expired-desc">
                This session has expired. Please log in again.
              </p>
              <button
                className="session-expired-ok-btn"
                ref={sessionExpiredBtnRef}
                onClick={handleSessionExpiredConfirm}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Primary Navigation - varies by page */}
      {NAV_PAGES.includes(location.pathname) && (
        <>
          <nav className="navigation" aria-label="Main navigation" onKeyDown={(e) => { if (e.key === 'Escape') { closeMenu(); setOpenDropdown(null); } }}>
            <ul className={`nav-links list-unstyled ${isMenuOpen ? "open" : ""}`} role="menubar">
              {(HOME_NAV_PAGES.includes(location.pathname) ? HOME_NAV : FULL_NAV).map((item) => {
                const isActive = location.pathname === item.to;
                const currentMenu = new URLSearchParams(location.search).get("menu");
                const isAssessmentActive = item.dropdown
                  ? currentMenu
                      ? currentMenu === item.menu
                      : (item.label === "Assessments" && location.pathname === "/continue-assessment") ||
                        (item.label === "Goals" && location.pathname === "/Goals")
                  : false;
                if (item.dropdown) {
                  return (
                    <li
                      key={item.label}
                      className={`nav-item nav-item-dropdown ${isAssessmentActive ? "active" : ""}`}
                    >
                      <button
                        type="button"
                        className="nav-item-dropdown-toggle"
                        aria-haspopup="true"
                        aria-expanded={openDropdown === item.label}
                        onClick={() => {
                          setOpenDropdown(openDropdown === item.label ? null : item.label);
                          if (openDropdown === item.label) closeMenu();
                        }}
                      >
                        {item.label}
                        <i className={`fas fa-caret-${openDropdown === item.label ? "up" : "down"} nav-dropdown-caret`} aria-hidden="true"></i>
                      </button>
                      {openDropdown === item.label && (
                        <ul className="nav-dropdown-menu list-unstyled" role="menu">
                          {item.dropdown.map((child) => {
                            const childActive = location.pathname === child.to.split("?")[0];
                            return (
                              <li key={child.to} role="none">
                                <button
                                  type="button"
                                  role="menuitem"
                                  className={`nav-dropdown-item ${childActive ? "active" : ""}`}
                                  aria-current={childActive ? "page" : undefined}
                                  onClick={() => {
                                    setOpenDropdown(null);
                                    closeMenu();
                                    navigate(child.to);
                                  }}
                                >
                                  {child.label}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                }
                return (
                  <li
                    key={item.label}
                    role="menuitem"
                    tabIndex={0}
                    className={`nav-item ${isActive ? "active" : ""}`}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => { closeMenu(); navigate(item.to); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        closeMenu();
                        navigate(item.to);
                      }
                    }}
                  >
                    {item.label}
                  </li>
                );
              })}
            </ul>
          </nav>
          {isMenuOpen && (
            <div
              className="menu-overlay"
              aria-hidden="true"
              onClick={closeMenu}
            ></div>
          )}
        </>
      )}
    </>
  );
};

export default Header;
